import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../services/auth.service";
import axios from "axios";
import XLSX from "xlsx";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import Push from "push.js";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

export default function VentasCruzadas() {
    const { user } = useAuth();
    
    // Helper function to check if user should be treated as admin
    // Returns true if user has ROLE_ADMIN or if ejecutivo.department_supervisor is true
    const isUserAdmin = () => {
        const hasAdminRole = user.roles && user.roles.includes("ROLE_ADMIN");
        const isDepartmentSupervisor = user.ejecutivo?.department_supervisor === true;
        return hasAdminRole || isDepartmentSupervisor;
    };
    
    // State management
    const [rawExcelData, setRawExcelData] = useState([]);
    const [currentImportFileName, setCurrentImportFileName] = useState(null);
    const [selectedColumns, setSelectedColumns] = useState({
        nombreCompleto: null,
        telefono: null,
        agente: null,
        otherColumns: []
    });
    const [showColumnMapping, setShowColumnMapping] = useState(false);
    const [availableColumns, setAvailableColumns] = useState([]);
    const [tableData, setTableData] = useState([]);
    // Removed filteredData - using server-side pagination now
    const [selectedRows, setSelectedRows] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [filters, setFilters] = useState({
        search: "",
        status: null,
        nombreCompleto: "",
        telefono: "",
        ejecutivo: null,
        agente: "",
        assignedUser: "" // For admin users to filter by assigned user email
    });
    const [includeOcultos, setIncludeOcultos] = useState(false);
    const [sortBy, setSortBy] = useState("nombreCompleto");
    const [sortOrder, setSortOrder] = useState("asc");
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showManageStatusesModal, setShowManageStatusesModal] = useState(false);
    const [newStatusName, setNewStatusName] = useState("");
    const [newStatusColor, setNewStatusColor] = useState("#3B82F6");
    const [editingStatus, setEditingStatus] = useState(null);
    const [editStatusName, setEditStatusName] = useState("");
    const [editStatusColor, setEditStatusColor] = useState("#3B82F6");
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        total: 0
    });
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedVariables, setSelectedVariables] = useState([]);
    const [selectedButtonParameters, setSelectedButtonParameters] = useState([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [selectedRowForNotes, setSelectedRowForNotes] = useState(null);
    const [newNote, setNewNote] = useState("");
    const [noteReminder, setNoteReminder] = useState("");
    const [noteReminderTime, setNoteReminderTime] = useState("");
    const [notes, setNotes] = useState([]);
    const [savedFiles, setSavedFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentFileName, setCurrentFileName] = useState(null);
    const [editingCell, setEditingCell] = useState(null);
    const [cellEditValue, setCellEditValue] = useState("");
    const [ejecutivos, setEjecutivos] = useState([]);
    const [selectedEjecutivo, setSelectedEjecutivo] = useState(null);
    const [massiveActionField, setMassiveActionField] = useState(null);
    const [massiveActionValue, setMassiveActionValue] = useState("");
    const [massiveActionEjecutivo, setMassiveActionEjecutivo] = useState(null);
    const [massiveActionStatus, setMassiveActionStatus] = useState(null);
    const [showManageFilesModal, setShowManageFilesModal] = useState(false);
    const [selectedFilesToDelete, setSelectedFilesToDelete] = useState([]);
    const [showRemindersCalendar, setShowRemindersCalendar] = useState(false);
    const [reminders, setReminders] = useState([]);
    // Statistics (admin only)
    const [stats, setStats] = useState({
        overview: {},
        byStatus: [],
        byEjecutivo: []
    });
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [statsError, setStatsError] = useState(null);
    const [statsEjecutivo, setStatsEjecutivo] = useState(null);
    const [statsDate, setStatsDate] = useState("");
    const [statsDateField, setStatsDateField] = useState("createdAt");
    
    // Configuration UI state (admin only)
    const [configurations, setConfigurations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [configFormData, setConfigFormData] = useState({
        name: "",
        description: "",
        departments: [],
        isActive: true
    });
    const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
    const [userConfiguration, setUserConfiguration] = useState(null);

    // Helper function to check if user is department supervisor
    const isDepartmentSupervisor = () => {
        return user?.ejecutivo?.department_supervisor === true;
    };

    // Helper function to check if user is admin (not department supervisor)
    const isAdminOnly = () => {
        return user?.roles?.includes("ROLE_ADMIN") && !isDepartmentSupervisor();
    };

    // Load user's configuration based on their department
    const loadUserConfiguration = async () => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/configs`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.get(url, config);
            console.log('Config API response:', response.data);
            
            if (response.data && Array.isArray(response.data)) {
                console.log('All configurations loaded:', response.data);
                
                // For ALL users, use the first active configuration
                // This ensures all users filter ejecutivos by the configured departments
                const matchingConfig = response.data.find(config => config.isActive) || null;
                
                console.log('Selected configuration (first active):', matchingConfig);
                if (matchingConfig && matchingConfig.departments) {
                    console.log('Configuration departments:', matchingConfig.departments);
                    const deptNames = matchingConfig.departments.map(dept => dept.name || dept._id);
                    console.log('Department names to filter:', deptNames);
                }
                
                setUserConfiguration(matchingConfig);
                console.log('User configuration set in state:', matchingConfig);
                
                // Return the config so it can be used immediately
                return matchingConfig;
            } else {
                console.log('No configurations found or invalid response:', response.data);
                setUserConfiguration(null);
                return null;
            }
        } catch (error) {
            console.error("Error loading user configuration:", error);
            console.error("Error details:", error.response?.data || error.message);
            setUserConfiguration(null);
            return null;
        }
    };

    // Load data from API on mount
    useEffect(() => {
        const initializeData = async () => {
            await loadStatuses();
            await loadTemplates();
            await loadReminders();
            
            // Load user's configuration for ALL users (admin, department supervisor, regular users)
            // IMPORTANT: Get the configuration directly from the function
            const config = await loadUserConfiguration();
            
            // Load ejecutivos (will be filtered by configuration via backend API)
            // Pass the configuration directly to avoid state timing issues
            await loadEjecutivos(config);
            
            // Load saved files (will respect department supervisor status)
            await loadSavedFiles();
            
            // Load configurations and departments if user is admin only (not department supervisor)
            // This is for the configuration UI management
            if (isAdminOnly()) {
                await loadConfigurations();
                await loadDepartments();
            }
            // Do not load data by default - wait for user to select a file
        };
        initializeData();
    }, []);

    // Reload ejecutivos when user configuration changes
    // This ensures ejecutivos are filtered correctly after configuration is loaded
    // Only reload if configuration is actually set (not null, not undefined)
    useEffect(() => {
        // Only reload if we have a user and configuration has been determined
        // Wait for the configuration to be loaded (not undefined)
        if (user && userConfiguration !== undefined) {
            console.log('User configuration changed, reloading ejecutivos. Config:', userConfiguration);
            // Pass the configuration directly to ensure it's used
            loadEjecutivos(userConfiguration);
        }
    }, [userConfiguration]);

    // Check for upcoming reminders and show notifications
    useEffect(() => {
        const checkUpcomingReminders = async () => {
            try {
                // Load upcoming reminders
                let url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/reminders`;
                const params = new URLSearchParams();
                params.append('upcomingOnly', 'true');
                
                // Note: For upcoming reminders, we don't filter by userId
                // Department supervisors and admins see all upcoming reminders
                
                url += '?' + params.toString();
                
                const config = {
                    headers: {
                        "x-access-token": user.accessToken,
                        "Content-Type": "application/json",
                    },
                };
                
                const response = await axios.get(url, config);
                const upcomingReminders = Array.isArray(response.data) ? response.data : (response.data?.data || []);
                
                // Check for reminders that are due soon (within next 15 minutes)
                const now = new Date();
                const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60 * 1000);
                
                upcomingReminders.forEach(reminder => {
                    // Handle nested note structure
                    const reminderDateValue = reminder.note?.reminder || reminder.reminder || reminder.reminderDate;
                    const reminderDate = new Date(reminderDateValue);
                    if (reminderDate >= now && reminderDate <= fifteenMinutesFromNow) {
                        // Show notification
                        const reminderText = reminder.note?.text || reminder.text || 'Recordatorio';
                        const contactName = reminder.nombreCompleto || reminder.contactName || 'Contacto';
                        const timeUntil = Math.round((reminderDate - now) / 60000); // minutes
                        
                        Push.create("Recordatorio", {
                            body: `${reminderText} - ${contactName} (en ${timeUntil} min)`,
                            icon: "/icon.png",
                            timeout: 5000,
                            onClick: function () {
                                window.focus();
                                this.close();
                            }
                        });
                        
                        // Also show toast notification
                        toast.info(`Recordatorio: ${reminderText} - ${contactName}`, {
                            autoClose: 5000
                        });
                    }
                });
            } catch (error) {
                console.error("Error checking upcoming reminders:", error);
            }
        };

        // Check immediately
        checkUpcomingReminders();
        
        // Check every minute
        const interval = setInterval(checkUpcomingReminders, 60000);
        
        return () => clearInterval(interval);
    }, [user]);

    // Load reminders from API
    const loadReminders = async (upcomingOnly = false) => {
        try {
            let url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/reminders`;
            const params = new URLSearchParams();
            
            // Only filter by userId if user is not admin and not department supervisor
            const isAdmin = user?.roles?.includes("ROLE_ADMIN");
            const isSupervisor = isDepartmentSupervisor();
            
            if (upcomingOnly) {
                params.append('upcomingOnly', 'true');
            } else if (!isAdmin && !isSupervisor && user && (user.ejecutivo?._id)) {
                params.append('userId', user.ejecutivo?._id);
            }
            
            if (params.toString()) {
                url += '?' + params.toString();
            }
            
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            
            const response = await axios.get(url, config);
            if (response.data) {
                const remindersData = Array.isArray(response.data) ? response.data : (response.data.data || []);
                setReminders(remindersData);
            }
        } catch (error) {
            console.error("Error loading reminders:", error);
        }
    };

    // Load ejecutivos (agentes) from API
    const loadEjecutivos = async (configToUse = null) => {
        try {
            // Use provided config or fall back to state
            const userConfig = configToUse !== null ? configToUse : userConfiguration;
            
            console.log('loadEjecutivos called with config:', userConfig);
            console.log('configToUse parameter:', configToUse);
            console.log('userConfiguration state:', userConfiguration);
            
            let url = `${process.env.REACT_APP_BACKEND_API}/ejecutivo`;
            const params = new URLSearchParams();
            
            let departmentNames = [];
            
            // Filter ejecutivos ONLY by departments from ventas-cruzadas configuration
            // This applies to ALL users (admin, department supervisor, and regular users)
            // Only use the department name from the ventas-cruzadas config
            if (userConfig && userConfig.departments && Array.isArray(userConfig.departments) && userConfig.departments.length > 0) {
                console.log('Processing departments from config:', userConfig.departments);
                
                // Get department names from the configuration
                // Only extract the name property from each department
                departmentNames = userConfig.departments
                    .map((dept, index) => {
                        console.log(`Processing department ${index}:`, dept);
                        // Handle different department object structures
                        if (typeof dept === 'string') {
                            return dept;
                        } else if (dept && typeof dept === 'object') {
                            // Only use the name property from the ventas-cruzadas config
                            const deptName = dept.name || null;
                            console.log(`Department ${index} name:`, deptName);
                            return deptName;
                        }
                        return null;
                    })
                    .filter(name => name !== null && name !== undefined && name !== '');
                
                console.log('Filtering ejecutivos by departments from ventas-cruzadas config:', departmentNames);
                console.log('User configuration:', userConfig);
            } else {
                console.log('No ventas-cruzadas configuration found. Loading all ejecutivos.');
                console.log('User configuration state:', userConfig);
                console.log('Has config?', !!userConfig);
                console.log('Has departments?', !!(userConfig && userConfig.departments));
                console.log('Is array?', Array.isArray(userConfig?.departments));
                console.log('Length?', userConfig?.departments?.length);
            }
            
            // Add department filter to query parameters if we have department names
            // The backend expects: /api/ejecutivo?departament=DepartmentName
            // For multiple departments, send them as multiple parameters (backend should handle this)
            if (departmentNames.length > 0) {
                departmentNames.forEach((deptName) => {
                    params.append('departament', deptName);
                });
            }
            
            // Add query parameters to URL if any
            if (params.toString()) {
                url += `?${params.toString()}`;
                console.log('Ejecutivos API URL with filters:', url);
            } else {
                console.log('Ejecutivos API URL (no filters):', url);
            }
            
            const axiosConfig = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.get(url, axiosConfig);
            if (response.data) {
                console.log('Loaded ejecutivos:', response.data.length);
                // Format ejecutivos for react-select
                const formattedEjecutivos = response.data.map(ejecutivo => ({
                    value: ejecutivo._id,
                    label: `${ejecutivo.firstName || ''} ${ejecutivo.lastName || ''}`.trim() || ejecutivo.email || 'Sin nombre',
                    ejecutivo: ejecutivo
                }));
                setEjecutivos(formattedEjecutivos);
            }
        } catch (error) {
            console.error("Error loading ejecutivos:", error);
            console.error("Error details:", error.response?.data || error.message);
        }
    };

    // Load statistics (admin only)
    const loadStats = async (options = {}) => {
        if (!user || !user.accessToken) return;

        // Only admins should be able to load statistics
        const isAdmin = user?.roles?.includes("ROLE_ADMIN");
        if (!isAdmin) return;

        setIsLoadingStats(true);
        setStatsError(null);

        try {
            let url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/stats`;
            const params = new URLSearchParams();

            if (options.ejecutivoId) {
                params.append("ejecutivo", options.ejecutivoId);
            }

            // Add date filter if provided
            if (options.date) {
                params.append("date", options.date);
            }
            if (options.dateField) {
                params.append("dateField", options.dateField);
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };

            const response = await axios.get(url, config);

            const data = response.data || {};

            // Use the new structure directly from backend
            setStats({
                total: data.total || 0,
                byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
                byEjecutivo: Array.isArray(data.byEjecutivo) ? data.byEjecutivo : [],
                filtersApplied: data.filtersApplied || {},
                raw: data
            });
        } catch (error) {
            console.error("Error loading ventas-cruzadas stats:", error);
            const errorMessage = error.response?.data?.message || error.message || "Error al cargar estadísticas";
            setStatsError(errorMessage);
            toast.error("Error al cargar estadísticas");
        } finally {
            setIsLoadingStats(false);
        }
    };

    // Load departments from API
    const loadDepartments = async () => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/department`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.get(url, config);
            if (response.data && response.data.length > 0) {
                setDepartments(response.data);
            }
        } catch (error) {
            console.error("Error loading departments:", error);
            toast.error("Error al cargar departamentos");
        }
    };

    // Load configurations from API
    const loadConfigurations = async () => {
        setIsLoadingConfigs(true);
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/configs`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.get(url, config);
            if (response.data) {
                setConfigurations(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error("Error loading configurations:", error);
            toast.error("Error al cargar configuraciones");
        } finally {
            setIsLoadingConfigs(false);
        }
    };

    // Create new configuration
    const createConfiguration = async () => {
        if (!configFormData.name || !configFormData.name.trim()) {
            toast.error("El nombre es requerido");
            return;
        }
        if (!configFormData.departments || configFormData.departments.length === 0) {
            toast.error("Debe seleccionar al menos un departamento");
            return;
        }

        setIsLoadingConfigs(true);
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/configs`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const payload = {
                name: configFormData.name.trim(),
                description: configFormData.description.trim() || undefined,
                departments: configFormData.departments.map(dept => dept.value || dept._id),
                isActive: configFormData.isActive
            };
            const response = await axios.post(url, payload, config);
            if (response.data) {
                toast.success("Configuración creada correctamente");
                setConfigurations([...configurations, response.data]);
                setShowConfigModal(false);
                resetConfigForm();
            }
        } catch (error) {
            console.error("Error creating configuration:", error);
            const errorMessage = error.response?.data?.message || error.message || "Error al crear configuración";
            toast.error(errorMessage);
        } finally {
            setIsLoadingConfigs(false);
        }
    };

    // Update configuration
    const updateConfiguration = async () => {
        if (!editingConfig) return;
        
        if (configFormData.name && !configFormData.name.trim()) {
            toast.error("El nombre no puede estar vacío");
            return;
        }
        if (configFormData.departments && configFormData.departments.length === 0) {
            toast.error("Debe seleccionar al menos un departamento");
            return;
        }

        setIsLoadingConfigs(true);
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/configs/${editingConfig._id}`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const payload = {};
            if (configFormData.name) payload.name = configFormData.name.trim();
            if (configFormData.description !== undefined) payload.description = configFormData.description.trim() || "";
            if (configFormData.departments) payload.departments = configFormData.departments.map(dept => dept.value || dept._id);
            if (configFormData.isActive !== undefined) payload.isActive = configFormData.isActive;

            const response = await axios.put(url, payload, config);
            if (response.data) {
                toast.success("Configuración actualizada correctamente");
                setConfigurations(configurations.map(config => 
                    config._id === editingConfig._id ? response.data : config
                ));
                setShowConfigModal(false);
                setEditingConfig(null);
                resetConfigForm();
            }
        } catch (error) {
            console.error("Error updating configuration:", error);
            const errorMessage = error.response?.data?.message || error.message || "Error al actualizar configuración";
            toast.error(errorMessage);
        } finally {
            setIsLoadingConfigs(false);
        }
    };

    // Delete configuration
    const deleteConfiguration = async (configId) => {
        if (!window.confirm("¿Está seguro de que desea eliminar esta configuración?")) {
            return;
        }

        setIsLoadingConfigs(true);
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/configs/${configId}`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            await axios.delete(url, config);
            toast.success("Configuración eliminada correctamente");
            setConfigurations(configurations.filter(config => config._id !== configId));
        } catch (error) {
            console.error("Error deleting configuration:", error);
            const errorMessage = error.response?.data?.message || error.message || "Error al eliminar configuración";
            toast.error(errorMessage);
        } finally {
            setIsLoadingConfigs(false);
        }
    };

    // Reset configuration form
    const resetConfigForm = () => {
        setConfigFormData({
            name: "",
            description: "",
            departments: [],
            isActive: true
        });
        setEditingConfig(null);
    };

    // Open configuration modal for create
    const openCreateConfigModal = () => {
        resetConfigForm();
        setShowConfigModal(true);
    };

    // Open configuration modal for edit
    const openEditConfigModal = (config) => {
        setEditingConfig(config);
        setConfigFormData({
            name: config.name || "",
            description: config.description || "",
            departments: config.departments ? config.departments.map(dept => ({
                value: dept._id,
                label: dept.name
            })) : [],
            isActive: config.isActive !== undefined ? config.isActive : true
        });
        setShowConfigModal(true);
    };

    // Load saved files list from API
    const loadSavedFiles = async () => {
        try {
            let url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/files`;
            
            // For non-admin and non-department-supervisor users, add owner parameter with ejecutivo._id
            const isAdmin = user.roles && user.roles.includes("ROLE_ADMIN");
            const isSupervisor = isDepartmentSupervisor();
            
            if (!isAdmin && !isSupervisor && user.ejecutivo?._id) {
                const params = new URLSearchParams();
                params.append('owner', user.ejecutivo._id);
                url += `?${params.toString()}`;
            }
            
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.get(url, config);
            if (response.data) {
                setSavedFiles(response.data);
            }
        } catch (error) {
            console.error("Error loading saved files:", error);
        }
    };

    // Load data from API with pagination and filters
    const loadDataFromAPI = async (filename = null, page = 1, pageSize = 25, filtersToApply = null, sortByParam = null, sortOrderParam = null) => {
        setIsLoading(true);
        try {
            let url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas`;
            const params = new URLSearchParams();
            
            // Add pagination parameters
            params.append('page', page.toString());
            params.append('pageSize', pageSize.toString());
            
            // Add sorting parameters
            const sortByToUse = sortByParam !== null ? sortByParam : sortBy;
            const sortOrderToUse = sortOrderParam !== null ? sortOrderParam : sortOrder;
            params.append('sortBy', sortByToUse);
            params.append('sortOrder', sortOrderToUse);
            
            // Add filename filter if provided
            if (filename) {
                params.append('filename', filename);
            }
            
            // Add filters if provided
            const filtersToUse = filtersToApply || filters;
            if (filtersToUse.search) {
                params.append('search', filtersToUse.search);
            }
            if (filtersToUse.status) {
                params.append('status', filtersToUse.status.toString());
            }
            if (filtersToUse.nombreCompleto) {
                params.append('nombreCompleto', filtersToUse.nombreCompleto);
            }
            if (filtersToUse.telefono) {
                params.append('telefono', filtersToUse.telefono);
            }
            if (filtersToUse.ejecutivo) {
                params.append('ejecutivo', filtersToUse.ejecutivo);
            }
            if (filtersToUse.agente) {
                params.append('agente', filtersToUse.agente);
            }
            
            // For admin users, add assignedUser filter if set
            const isAdmin = user.roles && user.roles.includes("ROLE_ADMIN");
            const isSupervisor = isDepartmentSupervisor();
            
            if (isAdmin && filtersToUse.assignedUser) {
                params.append('assignedUser', filtersToUse.assignedUser);
            }
            
            // For non-admin and non-department-supervisor users, add assignedUserId parameter with ejecutivo._id
            if (!isAdmin && !isSupervisor && user.ejecutivo?._id) {
                params.append('assignedUserId', user.ejecutivo._id);
                // Also add owner parameter for backward compatibility
                params.append('owner', user.ejecutivo._id);
            }
            
            // Add includeOcultos parameter if user wants to include hidden statuses
            if (includeOcultos) {
                params.append('includeOcultos', 'true');
            }
            
            url += `?${params.toString()}`;
            
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.get(url, config);
            if (response.data && response.data.data) {
                    // Ensure notes is always an array for each row and ejecutivo is properly formatted
                    const processedData = response.data.data.map(row => {
                        const processedRow = { ...row };
                        // Ensure notes is always an array
                        if (processedRow.notes) {
                            processedRow.notes = Array.isArray(processedRow.notes) ? processedRow.notes : [];
                        } else {
                            processedRow.notes = [];
                        }
                        // Ensure ejecutivo is properly formatted (object with value and label)
                        if (processedRow.ejecutivo && typeof processedRow.ejecutivo === 'object' && processedRow.ejecutivo.value && processedRow.ejecutivo.label) {
                            // Already formatted correctly
                        } else if (processedRow.ejecutivo && ejecutivos.length > 0) {
                            // Convert string/ID to object format if ejecutivos are loaded
                            const ejecutivoId = processedRow.ejecutivo?.value || processedRow.ejecutivo?._id || processedRow.ejecutivo;
                            const ejecutivoData = ejecutivos.find(e => e.value === ejecutivoId);
                            if (ejecutivoData) {
                                processedRow.ejecutivo = {
                                    value: ejecutivoData.value,
                                    label: ejecutivoData.label,
                                    selected: true
                                };
                            } else {
                                processedRow.ejecutivo = null;
                            }
                        } else {
                            processedRow.ejecutivo = null;
                        }
                        return processedRow;
                    });
                setTableData(processedData);
                setPagination(prev => ({ 
                    ...prev, 
                    page: page,
                    total: response.data.total || 0,
                    pageSize: pageSize
                }));
                if (filename) {
                    setCurrentFileName(filename);
                }
            }
        } catch (error) {
            console.error("Error loading data:", error);
            toast.error("Error al cargar los datos");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle file selection from dropdown
    const handleFileSelect = async (filename) => {
        if (filename) {
            setSelectedFile(filename);
            setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
            await loadDataFromAPI(filename, 1, pagination.pageSize);
            toast.success(`Datos de ${filename} cargados`);
        } else {
            setSelectedFile(null);
            setCurrentFileName(null);
            setPagination(prev => ({ ...prev, page: 1 }));
            await loadDataFromAPI(null, 1, pagination.pageSize);
        }
    };

    // Load templates from API
    const loadTemplates = async () => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/template`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.get(url, config);
            if (response.data) {
                // Handle different response structures
                let templatesArray = [];
                if (Array.isArray(response.data)) {
                    templatesArray = response.data;
                } else if (Array.isArray(response.data.data)) {
                    templatesArray = response.data.data;
                } else if (response.data.templates && Array.isArray(response.data.templates)) {
                    templatesArray = response.data.templates;
                } else if (Object.keys(response.data).length === 0) {
                    // Empty object response - just set empty array
                    templatesArray = [];
                } else {
                    // Only warn if it's not an empty object
                    console.warn("Templates response is not in expected format:", response.data);
                    templatesArray = [];
                }
                
                // Translate each template
                const translatedTemplates = templatesArray.map((template) => {
                    if (template.body) {
                        template.body = translateTemplate(template.body);
                    }
                    return template;
                });
                setTemplates(translatedTemplates);
            }
        } catch (error) {
            console.error("Error loading templates:", error);
            // Set empty array on error to prevent further issues
            setTemplates([]);
        }
    };

    // Translate template body - convert {{1}}, {{2}} to clickable spans
    const translateTemplate = (text) => {
        text = text.replace(/\{\{(\d+)\}\}/g, (match, number) => {
            return `<span data-type="variable" data-index="${number}" style="white-space: nowrap; cursor: pointer; background-color: #e3f2fd; padding: 2px 4px; border-radius: 3px; margin: 0 2px;">${number}</span>`;
        });
        // Add line breaks
        text = text.replace(/\n/g, '<br>');
        // Replace bold and italic formatting
        text = text.replace(/\*(.*?)\*/g, '<b>$1</b>');
        text = text.replace(/_(.*?)_/g, '<i>$1</i>');
        return text;
    };

    // Apply filters when filters change - triggers API call
    useEffect(() => {
        // Debounce filter changes to avoid too many API calls
        const timeoutId = setTimeout(() => {
            applyFilters();
        }, 500); // Wait 500ms after user stops typing

        return () => clearTimeout(timeoutId);
    }, [filters]);

    // Load statuses from API
    const loadStatuses = async () => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/statuses`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.get(url, config);
            if (response.data) {
                setStatuses(response.data);
            }
        } catch (error) {
            console.error("Error loading statuses:", error);
            // Initialize with default statuses if API fails
            setStatuses([
                { id: 1, name: "Nuevo", color: "#3B82F6" },
                { id: 2, name: "En Proceso", color: "#F59E0B" },
                { id: 3, name: "Contactado", color: "#10B981" },
                { id: 4, name: "Cerrado", color: "#8B5CF6" },
                { id: 5, name: "Rechazado", color: "#EF4444" }
            ]);
        }
    };

    // Read Excel file
    const readExcelFile = (file) => {
        toast.info(`Leyendo archivo ${file.name}`);
        const promise = new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsArrayBuffer(file);

            fileReader.onload = (e) => {
                const bufferArray = e.target.result;
                const wb = XLSX.read(bufferArray, { type: "buffer" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);
                resolve(data);
            };

            fileReader.onerror = (error) => {
                reject(error);
            };
        });

        promise.then((data) => {
            setRawExcelData(data);
            setCurrentImportFileName(file.name);
            if (data.length > 0) {
                // Collect all unique column names from all rows, not just the first row
                // This ensures columns with empty values in the first row are still included
                const allColumns = new Set();
                data.forEach(row => {
                    Object.keys(row).forEach(key => {
                        allColumns.add(key);
                    });
                });
                const columns = Array.from(allColumns);
                setAvailableColumns(columns);
                setShowColumnMapping(true);
                toast.success(`Archivo ${file.name} leído correctamente`);
            }
        }).catch((error) => {
            toast.error("Error al leer el archivo Excel");
            console.error(error);
        });
    };

    // Handle column mapping
    const handleColumnMapping = () => {
        if (!selectedColumns.nombreCompleto || !selectedColumns.telefono || !selectedColumns.agente) {
            toast.error("Debe seleccionar las columnas de Nombre Completo, Teléfono y Agente");
            return;
        }

        // Validate that columns are different from each other
        if (selectedColumns.telefono === selectedColumns.nombreCompleto) {
            toast.error("La columna de Teléfono debe ser diferente de la columna de Nombre Completo");
            return;
        }
        if (selectedColumns.agente === selectedColumns.nombreCompleto) {
            toast.error("La columna de Agente debe ser diferente de la columna de Nombre Completo");
            return;
        }
        if (selectedColumns.agente === selectedColumns.telefono) {
            toast.error("La columna de Agente debe ser diferente de la columna de Teléfono");
            return;
        }

        const mappedData = rawExcelData.map((row, index) => {
            // Get values, ensuring we handle null, undefined, and empty strings properly
            const nombreCompletoValue = row[selectedColumns.nombreCompleto];
            const telefonoValue = row[selectedColumns.telefono];
            const agenteValue = row[selectedColumns.agente];
            
            // Process nombreCompleto
            const nombreCompletoProcessed = (nombreCompletoValue !== null && nombreCompletoValue !== undefined && nombreCompletoValue !== "") 
                ? String(nombreCompletoValue).trim() 
                : "";
            
            // Process telefono - ensure it's not the same as nombreCompleto
            let telefonoProcessed = (telefonoValue !== null && telefonoValue !== undefined && telefonoValue !== "") 
                ? String(telefonoValue).trim() 
                : "";
            
            // Validation: if telefono is empty or same as nombreCompleto, set to empty string
            if (telefonoProcessed === nombreCompletoProcessed || telefonoProcessed === "") {
                telefonoProcessed = "";
            }
            
            // Process agente - find matching ejecutivo or use the value as string
            let ejecutivoProcessed = null;
            if (agenteValue !== null && agenteValue !== undefined && agenteValue !== "") {
                const agenteString = String(agenteValue).trim();
                // Try to find matching ejecutivo by name or email
                const matchingEjecutivo = ejecutivos.find(e => {
                    const ejecutivoName = e.label.toLowerCase();
                    const ejecutivoEmail = e.ejecutivo?.email?.toLowerCase() || "";
                    const searchValue = agenteString.toLowerCase();
                    return ejecutivoName.includes(searchValue) || ejecutivoEmail.includes(searchValue) || e.value === agenteString;
                });
                
                if (matchingEjecutivo) {
                    ejecutivoProcessed = {
                        value: matchingEjecutivo.value,
                        label: matchingEjecutivo.label,
                        selected: true
                    };
                } else {
                    // If no match found, store as string value (will need manual assignment later)
                    ejecutivoProcessed = {
                        value: agenteString,
                        label: agenteString,
                        selected: false
                    };
                }
            }
            
            // Build the mapped row with all selected columns
            const mappedRow = {
                _id: `temp_${index}`,
                nombreCompleto: nombreCompletoProcessed,
                telefono: telefonoProcessed,
                status: statuses[0]?.id || 1,
                selected: false,
                notes: [], // Initialize notes as empty array
                filename: currentImportFileName, // Store the filename
                importedAt: new Date().toISOString(), // Store import timestamp
                ejecutivo: ejecutivoProcessed // Add ejecutivo from Excel agente column
            };
            
            // Add all selected other columns, ensuring they exist in every row
            // If a column doesn't exist in this row, set it to empty string
            selectedColumns.otherColumns.forEach(col => {
                if (col.selected) {
                    const value = row[col.column];
                    if (value !== null && value !== undefined && value !== "") {
                        mappedRow[col.column] = String(value).trim();
                    } else {
                        // Ensure the field exists even if empty
                        mappedRow[col.column] = "";
                    }
                }
            });
            
            return mappedRow;
        });

        setTableData(mappedData);
        setShowColumnMapping(false);
        toast.success("Columnas mapeadas correctamente");
        
        // Save to API - pass the filename explicitly
        saveDataToAPI(mappedData, currentImportFileName);
    };

    // Save data to API
    const saveDataToAPI = async (data, filename = null) => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            
            // Use the passed filename or fallback to currentImportFileName
            const fileToUse = filename || currentImportFileName;
            
            // Remove temporary _id fields (those starting with "temp_") before sending to API
            // MongoDB will generate proper ObjectIds for new records
            // Ensure filename and importedAt are preserved
            const cleanedData = data.map(row => {
                const cleanedRow = { ...row };
                // If _id is a temporary ID (starts with "temp_"), remove it
                if (cleanedRow._id && cleanedRow._id.toString().startsWith('temp_')) {
                    delete cleanedRow._id;
                }
                // Always set filename - prioritize passed filename, then row filename, then currentImportFileName
                if (fileToUse) {
                    cleanedRow.filename = fileToUse;
                } else if (!cleanedRow.filename || cleanedRow.filename === null || cleanedRow.filename === undefined || cleanedRow.filename === "") {
                    console.warn("No filename available for row, using default");
                    cleanedRow.filename = "unknown.xlsx";
                }
                // Ensure importedAt is included if not present
                if (!cleanedRow.importedAt || cleanedRow.importedAt === null || cleanedRow.importedAt === undefined) {
                    cleanedRow.importedAt = new Date().toISOString();
                }
                return cleanedRow;
            });
            
            // Debug: Log first row to verify filename is included
            if (cleanedData.length > 0) {
                console.log("Saving data with filename:", cleanedData[0].filename);
                console.log("Total records to save:", cleanedData.length);
            }
            
            const response = await axios.post(url, { data: cleanedData }, config);
            if (response.data) {
                // Reload saved files list after import
                await loadSavedFiles();
                // Reload data with pagination - reset to first page
                setPagination(prev => ({ ...prev, page: 1 }));
                await loadDataFromAPI(currentImportFileName, 1, pagination.pageSize);
                toast.success("Datos guardados correctamente");
            }
        } catch (error) {
            console.error("Error saving data:", error);
            if (error.response) {
                console.error("Error response:", error.response.data);
            }
            toast.error("Error al guardar los datos");
        }
    };

    // Create new status
    const createStatus = async () => {
        if (!newStatusName.trim()) {
            toast.error("El nombre del estado es requerido");
            return;
        }

        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/statuses`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const newStatus = {
                name: newStatusName,
                color: newStatusColor
            };
            const response = await axios.post(url, newStatus, config);
            if (response.data) {
                setStatuses([...statuses, response.data]);
                setNewStatusName("");
                setShowStatusModal(false);
                toast.success("Estado creado correctamente");
            }
        } catch (error) {
            console.error("Error creating status:", error);
            toast.error("Error al crear el estado");
        }
    };

    // Update status
    const updateStatus = async () => {
        if (!editStatusName.trim()) {
            toast.error("El nombre del estado es requerido");
            return;
        }

        if (!editingStatus) {
            toast.error("No hay estado seleccionado para editar");
            return;
        }

        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/statuses/${editingStatus.id}`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const updatedStatus = {
                name: editStatusName,
                color: editStatusColor
            };
            const response = await axios.put(url, updatedStatus, config);
            if (response.data) {
                setStatuses(statuses.map(s => s.id === editingStatus.id ? response.data : s));
                setEditingStatus(null);
                setEditStatusName("");
                setEditStatusColor("#3B82F6");
                toast.success("Estado actualizado correctamente");
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Error al actualizar el estado");
        }
    };

    // Delete status
    const deleteStatus = async (statusId) => {
        if (!window.confirm("¿Está seguro de que desea eliminar este estado? Esta acción no se puede deshacer.")) {
            return;
        }

        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/statuses/${statusId}`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            await axios.delete(url, config);
            setStatuses(statuses.filter(s => s.id !== statusId));
            toast.success("Estado eliminado correctamente");
        } catch (error) {
            console.error("Error deleting status:", error);
            toast.error("Error al eliminar el estado. Asegúrese de que no esté en uso.");
        }
    };

    // Toggle ocultar status
    const toggleOcultarStatus = async (statusId, ocultar) => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/statuses/${statusId}`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.put(url, { ocultar }, config);
            if (response.data) {
                setStatuses(statuses.map(s => s.id === statusId ? { ...s, ocultar: response.data.ocultar } : s));
                toast.success(`Estado ${ocultar ? 'ocultado' : 'mostrado'} correctamente`);
            }
        } catch (error) {
            console.error("Error toggling ocultar status:", error);
            toast.error("Error al cambiar la visibilidad del estado");
        }
    };

    // Start editing a status
    const startEditingStatus = (status) => {
        setEditingStatus(status);
        setEditStatusName(status.name);
        setEditStatusColor(status.color);
    };

    // Cancel editing status
    const cancelEditingStatus = () => {
        setEditingStatus(null);
        setEditStatusName("");
        setEditStatusColor("#3B82F6");
    };

    // Update row status
    const updateRowStatus = async (rowId, statusId) => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/${rowId}`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            const response = await axios.put(url, { status: statusId }, config);
            if (response.data) {
                setTableData(tableData.map(row => 
                    row._id === rowId ? { ...row, status: statusId } : row
                ));
                toast.success("Estado actualizado");
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Error al actualizar el estado");
        }
    };

    // Apply filters - triggers API call with filters
    const applyFilters = async () => {
        // Reset to first page when filters change
        setPagination(prev => ({ ...prev, page: 1 }));
        await loadDataFromAPI(selectedFile, 1, pagination.pageSize, filters);
    };

    // Handle column sorting
    const handleColumnSort = async (columnName) => {
        let newSortOrder = 'asc';
        // If clicking the same column, toggle sort order
        if (sortBy === columnName) {
            newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        // If clicking a different column, default to ascending
        setSortBy(columnName);
        setSortOrder(newSortOrder);
        // Reset to first page when sorting changes
        setPagination(prev => ({ ...prev, page: 1 }));
        await loadDataFromAPI(selectedFile, 1, pagination.pageSize, filters, columnName, newSortOrder);
    };

    // Handle file deletion
    const handleDeleteFiles = async () => {
        if (selectedFilesToDelete.length === 0) {
            toast.error("Seleccione al menos un archivo para eliminar");
            return;
        }

        if (!window.confirm(`¿Está seguro de que desea eliminar ${selectedFilesToDelete.length} archivo(s)? Esta acción no se puede deshacer.`)) {
            return;
        }

        setIsLoading(true);
        let successCount = 0;
        let errorCount = 0;

        try {
            const deletePromises = selectedFilesToDelete.map(async (index) => {
                try {
                    const file = savedFiles[index];
                    const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/files/${encodeURIComponent(file.filename)}`;
                    const config = {
                        headers: {
                            "x-access-token": user.accessToken,
                            "Content-Type": "application/json",
                        },
                    };
                    await axios.delete(url, config);
                    successCount++;
                } catch (error) {
                    console.error(`Error deleting file ${savedFiles[index].filename}:`, error);
                    errorCount++;
                }
            });

            await Promise.all(deletePromises);

            // Reload files list
            await loadSavedFiles();

            // If current file was deleted, clear selection
            const deletedFilenames = selectedFilesToDelete.map(index => savedFiles[index].filename);
            if (selectedFile && deletedFilenames.includes(selectedFile)) {
                setSelectedFile(null);
                setCurrentFileName(null);
                setTableData([]);
                setPagination(prev => ({ ...prev, total: 0, page: 1 }));
            }

            // Clear selection
            setSelectedFilesToDelete([]);

            if (errorCount === 0) {
                toast.success(`${successCount} archivo(s) eliminado(s) correctamente`);
            } else {
                toast.warning(`${successCount} archivo(s) eliminado(s), ${errorCount} error(es)`);
            }

            // Close modal if all files were deleted or if current file was deleted
            if (savedFiles.length === selectedFilesToDelete.length || (selectedFile && deletedFilenames.includes(selectedFile))) {
                setShowManageFilesModal(false);
            }
        } catch (error) {
            console.error("Error deleting files:", error);
            toast.error("Error al eliminar los archivos");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle row selection
    const toggleRowSelection = (rowId) => {
        setSelectedRows(prev => 
            prev.includes(rowId) 
                ? prev.filter(id => id !== rowId)
                : [...prev, rowId]
        );
    };

    const toggleAllRows = () => {
        if (selectedRows.length === paginatedData.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(paginatedData.map(row => row._id));
        }
    };

    // Handle template selection
    const handleTemplateChange = (e) => {
        const templateId = e.target.value;
        if (templateId) {
            const template = templates.find(t => t._id === templateId);
            setSelectedTemplate(template);
            setSelectedVariables([]);
            setSelectedButtonParameters([]);
            if (selectedRows.length > 0) {
                setShowTemplateModal(true);
            }
        } else {
            setSelectedTemplate(null);
            setSelectedVariables([]);
        }
    };

    // Handle variable selection from column click
    const handleColumnClickForVariable = (columnName) => {
        if (!selectedTemplate) {
            toast.error("Seleccione una plantilla primero");
            return;
        }

        const currentIndex = selectedVariables.length;
        if (currentIndex >= selectedTemplate.parameters.length) {
            // Check if we can add to button parameters
            if (selectedTemplate.buttonparameters && selectedTemplate.buttonparameters.length > 0 && 
                selectedButtonParameters.length < selectedTemplate.buttonparameters.length) {
                setSelectedButtonParameters([...selectedButtonParameters, columnName]);
                toast.info("Variable agregada a botones");
            } else {
                toast.error("No puedes agregar más variables");
            }
            return;
        }

        setSelectedVariables([...selectedVariables, columnName]);
    };

    // Update template preview with selected variables
    useEffect(() => {
        if (!selectedTemplate || selectedVariables.length === 0) return;
        
        const spans = document.querySelectorAll("span[data-type=variable]");
        selectedVariables.forEach((variable, index) => {
            if (spans[index]) {
                spans[index].textContent = variable;
                spans[index].style.backgroundColor = "#4caf50";
                spans[index].style.color = "white";
            }
        });
    }, [selectedVariables, selectedTemplate]);

    // Send WhatsApp action
    const handleSendWhatsApp = async (rowIds = null) => {
        const idsToSend = rowIds || selectedRows;
        
        if (idsToSend.length === 0) {
            toast.error("Seleccione al menos una fila");
            return;
        }

        if (!selectedTemplate) {
            toast.error("Seleccione una plantilla de WhatsApp");
            return;
        }

        if (selectedVariables.length !== selectedTemplate.parameters.length) {
            toast.error(`Debe seleccionar ${selectedTemplate.parameters.length} variable(s) para la plantilla`);
            return;
        }

        const selectedData = paginatedData.filter(row => idsToSend.includes(row._id));
        
        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/send-whatsapp`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };
            
            const payload = {
                rowIds: idsToSend,
                templateId: selectedTemplate._id,
                templateName: selectedTemplate.name,
                parameters: selectedVariables,
                buttonParameters: selectedButtonParameters
            };
            
            const response = await axios.post(url, payload, config);
            if (response.data) {
                toast.success(`WhatsApp enviado a ${idsToSend.length} contacto(s)`);
                if (!rowIds) {
                    setSelectedRows([]);
                    setSelectedTemplate(null);
                    setSelectedVariables([]);
                    setShowTemplateModal(false);
                }
            }
        } catch (error) {
            console.error("Error sending WhatsApp:", error);
            toast.error("Error al enviar WhatsApp");
        }
    };

    // Use tableData directly since pagination is handled server-side
    const paginatedData = tableData;

    // Get status by ID
    const getStatusById = (statusId) => {
        const found = statuses.find(s => s.id === statusId);
        if (found) return found;
        // Return default status if statuses array has items, otherwise return a default object
        if (statuses.length > 0) {
            return statuses[0];
        }
        // Return a default status object if no statuses are available
        return { id: 1, name: "Sin estado", color: "#6B7280" };
    };

    // Open notes modal for a row
    const openNotesModal = (row) => {
        setSelectedRowForNotes(row);
        // Ensure notes is always an array
        const rowNotes = row.notes || [];
        const notesArray = Array.isArray(rowNotes) ? rowNotes : [];
        setNotes(notesArray);
        setNewNote("");
        setNoteReminder("");
        setNoteReminderTime("");
        setShowNotesModal(true);
    };

    // Add a new note
    const handleAddNote = async () => {
        if (!newNote.trim()) {
            toast.error("La nota no puede estar vacía");
            return;
        }

        if (!selectedRowForNotes) {
            toast.error("No hay fila seleccionada");
            return;
        }

        const noteData = {
            text: newNote.trim(),
            timestamp: new Date().toISOString(),
            user: user.username || user.email || "Usuario",
            userId: user._id || user.id,
            reminder: noteReminder && noteReminderTime 
                ? new Date(`${noteReminder}T${noteReminderTime}`).toISOString()
                : null
        };

        try {
            const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/${selectedRowForNotes._id}`;
            const config = {
                headers: {
                    "x-access-token": user.accessToken,
                    "Content-Type": "application/json",
                },
            };

            // Get current notes or initialize empty array
            const currentNotes = selectedRowForNotes.notes || [];
            const updatedNotes = [...currentNotes, noteData];

            // Update the row with new notes
            const response = await axios.put(url, { notes: updatedNotes }, config);
            
            if (response.data) {
                // Ensure notes is always an array
                const responseNotes = response.data.data?.notes || response.data.notes || updatedNotes;
                const notesArray = Array.isArray(responseNotes) ? responseNotes : [];
                
                // Update local state
                const updatedRow = { ...selectedRowForNotes, notes: notesArray };
                setTableData(tableData.map(row => 
                    row._id === selectedRowForNotes._id ? updatedRow : row
                ));
                setNotes(notesArray);
                setSelectedRowForNotes(updatedRow);
                setNewNote("");
                setNoteReminder("");
                setNoteReminderTime("");
                toast.success("Nota agregada correctamente");
                // Reload reminders if a reminder was added
                if (noteData.reminder) {
                    await loadReminders();
                }
            }
        } catch (error) {
            console.error("Error adding note:", error);
            toast.error("Error al agregar la nota");
        }
    };

    // Format timestamp for display
    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Handle cell edit start
    const handleCellEditStart = (rowId, fieldName, currentValue) => {
        setEditingCell({ rowId, fieldName });
        setCellEditValue(currentValue || "");
    };

    // Handle cell edit save
    const handleCellEditSave = async (rowId, fieldName, newValue) => {
        if (editingCell && editingCell.rowId === rowId && editingCell.fieldName === fieldName) {
            const originalValue = tableData.find(r => r._id === rowId)?.[fieldName];
            
            // Only save if value changed
            if (String(newValue).trim() !== String(originalValue || "").trim()) {
                try {
                    const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/${rowId}`;
                    const config = {
                        headers: {
                            "x-access-token": user.accessToken,
                            "Content-Type": "application/json",
                        },
                    };

                    const updateData = { [fieldName]: newValue.trim() || "" };
                    const response = await axios.put(url, updateData, config);
                    
                    if (response.data) {
                        // Update local state
                        setTableData(tableData.map(row => 
                            row._id === rowId ? { ...row, [fieldName]: newValue.trim() || "" } : row
                        ));
                    }
                } catch (error) {
                    console.error("Error updating cell:", error);
                    toast.error("Error al guardar los cambios");
                    // Revert to original value on error
                    setTableData(tableData.map(row => 
                        row._id === rowId ? { ...row, [fieldName]: originalValue } : row
                    ));
                }
            }
            
            setEditingCell(null);
            setCellEditValue("");
        }
    };

    // Handle cell blur (save on click outside)
    const handleCellBlur = (rowId, fieldName) => {
        if (editingCell && editingCell.rowId === rowId && editingCell.fieldName === fieldName) {
            handleCellEditSave(rowId, fieldName, cellEditValue);
        }
    };

    // Handle cell key press (Enter to save, Escape to cancel)
    const handleCellKeyDown = (e, rowId, fieldName) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCellEditSave(rowId, fieldName, cellEditValue);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingCell(null);
            setCellEditValue("");
        }
    };

    // Handle massive action - update multiple rows at once
    const handleMassiveAction = async () => {
        if (selectedRows.length < 2) {
            toast.error("Seleccione al menos 2 filas para realizar una acción masiva");
            return;
        }

        if (!massiveActionField) {
            toast.error("Seleccione un campo para actualizar");
            return;
        }

        // Validate based on field type
        if (massiveActionField === 'ejecutivo' && !massiveActionEjecutivo) {
            toast.error("Seleccione un ejecutivo");
            return;
        }
        if (massiveActionField === 'status' && !massiveActionStatus) {
            toast.error("Seleccione un estado");
            return;
        }
        if (massiveActionField === 'notes' && !massiveActionValue.trim()) {
            toast.error("Escriba una nota");
            return;
        }
        if (massiveActionField !== 'ejecutivo' && massiveActionField !== 'status' && massiveActionField !== 'notes' && !massiveActionValue.trim()) {
            toast.error("Ingrese un valor");
            return;
        }

        setIsLoading(true);
        let successCount = 0;
        let errorCount = 0;

        try {
            // Prepare update data based on field type
            let updateData = {};
            
            if (massiveActionField === 'ejecutivo') {
                updateData.ejecutivo = {
                    value: massiveActionEjecutivo.value,
                    label: massiveActionEjecutivo.label,
                    selected: true
                };
            } else if (massiveActionField === 'status') {
                updateData.status = massiveActionStatus;
            } else if (massiveActionField === 'notes') {
                // For notes, we need to add to existing notes array
                const noteData = {
                    text: massiveActionValue.trim(),
                    timestamp: new Date().toISOString(),
                    user: user.username || user.email || "Usuario",
                    userId: user._id || user.id
                };
                // We'll handle notes separately as they need to be appended
            } else {
                // Normal field
                updateData[massiveActionField] = massiveActionValue.trim();
            }

            // Update all selected rows
            const updatePromises = selectedRows.map(async (rowId) => {
                try {
                    const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/${rowId}`;
                    const config = {
                        headers: {
                            "x-access-token": user.accessToken,
                            "Content-Type": "application/json",
                        },
                    };

                    if (massiveActionField === 'notes') {
                        // For notes, get current notes and append new one
                        const row = tableData.find(r => r._id === rowId);
                        const currentNotes = row?.notes || [];
                        const updatedNotes = [...currentNotes, {
                            text: massiveActionValue.trim(),
                            timestamp: new Date().toISOString(),
                            user: user.username || user.email || "Usuario",
                            userId: user._id || user.id
                        }];
                        await axios.put(url, { notes: updatedNotes }, config);
                    } else {
                        await axios.put(url, updateData, config);
                    }
                    successCount++;
                } catch (error) {
                    console.error(`Error updating row ${rowId}:`, error);
                    errorCount++;
                }
            });

            await Promise.all(updatePromises);

            // Update local state
            setTableData(tableData.map(row => {
                if (selectedRows.includes(row._id)) {
                    if (massiveActionField === 'ejecutivo') {
                        return { ...row, ejecutivo: updateData.ejecutivo };
                    } else if (massiveActionField === 'status') {
                        return { ...row, status: updateData.status };
                    } else if (massiveActionField === 'notes') {
                        const noteData = {
                            text: massiveActionValue.trim(),
                            timestamp: new Date().toISOString(),
                            user: user.username || user.email || "Usuario",
                            userId: user._id || user.id
                        };
                        return { ...row, notes: [...(row.notes || []), noteData] };
                    } else {
                        return { ...row, [massiveActionField]: updateData[massiveActionField] };
                    }
                }
                return row;
            }));

            // Reset massive action state
            setMassiveActionField(null);
            setMassiveActionValue("");
            setMassiveActionEjecutivo(null);
            setMassiveActionStatus(null);

            if (errorCount === 0) {
                toast.success(`Actualizado correctamente en ${successCount} registro(s)`);
            } else {
                toast.warning(`Actualizado en ${successCount} registro(s), ${errorCount} error(es)`);
            }
        } catch (error) {
            console.error("Error in massive action:", error);
            toast.error("Error al realizar la acción masiva");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="w-full">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Ventas Cruzadas</h1>
                            <p className="text-gray-600 mt-1">Gestión de datos</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => document.getElementById("excel-file-input").click()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Importar Excel
                            </button>
                            <button
                                onClick={() => setShowStatusModal(true)}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                                Nuevo Estado
                            </button>
                            <button
                                onClick={() => {
                                    setShowManageStatusesModal(true);
                                    setEditingStatus(null);
                                }}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Gestionar Estados
                            </button>
                            <button
                                onClick={() => {
                                    setShowManageFilesModal(true);
                                    setSelectedFilesToDelete([]);
                                }}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                                </svg>
                                Gestionar
                            </button>
                        </div>
                    </div>

                    {/* Saved Files Dropdown */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Seleccionar Archivo Guardado
                        </label>
                        <div className="flex items-center gap-3">
                            <select
                                value={selectedFile || ""}
                                onChange={(e) => handleFileSelect(e.target.value || null)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Todos los registros</option>
                                {savedFiles.map((file, index) => (
                                    <option key={index} value={file.filename}>
                                        {file.filename} - {formatTimestamp(file.importedAt)}
                                    </option>
                                ))}
                            </select>
                            {currentFileName && (
                                <span className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                                    Archivo actual: {currentFileName}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Statistics (Admin Only) */}
                    {user?.roles?.includes("ROLE_ADMIN") && (
                            <div className="mb-6 bg-white rounded-xl shadow-sm p-5 border border-gray-200">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Estadísticas de Ventas Cruzadas</h2>
                                    <p className="text-sm text-gray-600">
                                        Vea un resumen general o filtre por ejecutivo.
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => {
                                            setStatsEjecutivo(null);
                                            setStatsDate("");
                                            setStatsDateField("createdAt");
                                            loadStats({});
                                        }}
                                        disabled={isLoadingStats}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                                    >
                                        {isLoadingStats && (
                                            <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                        )}
                                        <span>Estadísticas Generales</span>
                                    </button>
                                </div>
                            </div>

                            {/* Filtros de estadísticas */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Ejecutivo
                                    </label>
                                    <Select
                                        value={statsEjecutivo}
                                        onChange={(selected) => setStatsEjecutivo(selected)}
                                        options={ejecutivos}
                                        placeholder="Seleccione un ejecutivo"
                                        isClearable
                                        className="text-sm"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: "38px",
                                                fontSize: "14px",
                                            }),
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Campo de Fecha
                                    </label>
                                    <select
                                        value={statsDateField}
                                        onChange={(e) => setStatsDateField(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    >
                                        <option value="createdAt">Fecha de Creación</option>
                                        <option value="updatedAt">Fecha de Actualización</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Fecha
                                    </label>
                                    <input
                                        type="date"
                                        value={statsDate}
                                        onChange={(e) => setStatsDate(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    />
                                </div>

                                <div className="flex items-end">
                                    <button
                                        onClick={() =>
                                            loadStats({
                                                ejecutivoId: statsEjecutivo?.value,
                                                date: statsDate || undefined,
                                                dateField: statsDate ? statsDateField : undefined,
                                            })
                                        }
                                        disabled={isLoadingStats || (!statsEjecutivo && !statsDate)}
                                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                    >
                                        Ver Estadísticas Filtradas
                                    </button>
                                </div>
                            </div>

                            {/* Mostrar filtros aplicados */}
                            {stats?.filtersApplied && Object.keys(stats.filtersApplied).length > 0 && (
                                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-sm font-semibold text-gray-700 mb-2">Filtros Aplicados:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {stats.filtersApplied.dateFilter && (
                                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                                📅 {stats.filtersApplied.dateFilter.dateField || 'createdAt'}: {stats.filtersApplied.dateFilter.date}
                                            </span>
                                        )}
                                        {stats.filtersApplied.ejecutivo && (
                                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                                👤 Ejecutivo: {stats.filtersApplied.ejecutivo}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {statsError && (
                                <p className="text-sm text-red-600 mb-3">
                                    {statsError}
                                </p>
                            )}

                            {stats && (
                                <>
                                    {/* Tarjeta de total */}
                                    <div className="mb-6">
                                        <div 
                                            className="text-white rounded-xl p-6 shadow-md flex items-center justify-between"
                                            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #9333ea 100%)' }}
                                        >
                                            <div>
                                                <p className="text-4xl font-bold">
                                                    {stats.total || 0}
                                                </p>
                                                <p className="text-sm opacity-80 mt-1">
                                                    Total Registros
                                                </p>
                                            </div>
                                            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                                                <span className="text-2xl">📊</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Distribución por estado */}
                                    {Array.isArray(stats.byStatus) && stats.byStatus.length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                                Distribución por Estado
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                {stats.byStatus
                                                    .filter(item => !item.ocultar)
                                                    .map((item, index) => (
                                                        <div
                                                            key={item.statusId || index}
                                                            className="px-4 py-3 rounded-lg border border-gray-200 bg-white flex items-center justify-between shadow-sm hover:shadow-md transition-shadow"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div
                                                                    className="w-4 h-4 rounded-full"
                                                                    style={{ backgroundColor: item.color || '#3B82F6' }}
                                                                ></div>
                                                                <span className="font-medium text-gray-800">
                                                                    {item.name || "Estado"}
                                                                </span>
                                                            </div>
                                                            <span className="text-lg font-bold text-gray-600">
                                                                {item.count || 0}
                                                            </span>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Distribución por ejecutivo con gráfico */}
                                    {Array.isArray(stats.byEjecutivo) && stats.byEjecutivo.length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                                Distribución por Ejecutivo
                                            </h3>
                                            
                                            {/* Gráfico de barras */}
                                            <div className="mb-6 bg-gray-50 rounded-lg p-4">
                                                <ResponsiveContainer width="100%" height={400}>
                                                    <BarChart
                                                        data={stats.byEjecutivo.map(item => ({
                                                            name: item.ejecutivoName || item.ejecutivoNombre || 'Sin nombre',
                                                            total: item.total || 0
                                                        }))}
                                                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis 
                                                            dataKey="name" 
                                                            angle={-45}
                                                            textAnchor="end"
                                                            height={100}
                                                            interval={0}
                                                        />
                                                        <YAxis />
                                                        <Tooltip />
                                                        <Legend />
                                                        <Bar dataKey="total" fill="#6366f1" name="Total Registros" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>

                                            {/* Tabla detallada por ejecutivo */}
                                            <div className="space-y-4">
                                                {stats.byEjecutivo.map((ejecutivo, index) => (
                                                    <div
                                                        key={ejecutivo.ejecutivoId || index}
                                                        className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                                                    >
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div>
                                                                <h4 className="font-semibold text-gray-900">
                                                                    {ejecutivo.ejecutivoName || ejecutivo.ejecutivoNombre || 'Sin nombre'}
                                                                </h4>
                                                                {ejecutivo.ejecutivoEmail && (
                                                                    <p className="text-sm text-gray-500">
                                                                        {ejecutivo.ejecutivoEmail}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-2xl font-bold text-gray-900">
                                                                    {ejecutivo.total || 0}
                                                                </p>
                                                                <p className="text-xs text-gray-500">Total</p>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Estados por ejecutivo */}
                                                        {Array.isArray(ejecutivo.byStatus) && ejecutivo.byStatus.length > 0 && (
                                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                                                <p className="text-xs font-medium text-gray-600 mb-2">Por Estado:</p>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {ejecutivo.byStatus
                                                                        .filter(item => !item.ocultar)
                                                                        .map((statusItem, statusIndex) => (
                                                                            <div
                                                                                key={statusItem.statusId || statusIndex}
                                                                                className="px-2 py-1 rounded text-xs flex items-center gap-1.5"
                                                                                style={{ 
                                                                                    backgroundColor: statusItem.color ? `${statusItem.color}20` : '#f3f4f6',
                                                                                    border: `1px solid ${statusItem.color || '#d1d5db'}`
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    className="w-2 h-2 rounded-full"
                                                                                    style={{ backgroundColor: statusItem.color || '#3B82F6' }}
                                                                                ></div>
                                                                                <span className="font-medium text-gray-700">
                                                                                    {statusItem.name || 'Estado'}
                                                                                </span>
                                                                                <span className="text-gray-600">
                                                                                    {statusItem.count || 0}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Action Bar */}
                    {selectedRows.length > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-blue-900 font-medium">
                                    {selectedRows.length} fila(s) seleccionada(s)
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setSelectedRows([]);
                                            setMassiveActionField(null);
                                            setMassiveActionValue("");
                                            setMassiveActionEjecutivo(null);
                                            setMassiveActionStatus(null);
                                        }}
                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                    >
                                        Deseleccionar
                                    </button>
                                </div>
                            </div>
                            
                            {/* Massive Action - Show when 2+ rows selected */}
                            {selectedRows.length >= 2 && (
                                <div className="mt-4 pt-4 border-t border-blue-200">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Acción Masiva</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Seleccionar Campo
                                            </label>
                                            <select
                                                value={massiveActionField || ""}
                                                onChange={(e) => {
                                                    setMassiveActionField(e.target.value || null);
                                                    setMassiveActionValue("");
                                                    setMassiveActionEjecutivo(null);
                                                    setMassiveActionStatus(null);
                                                }}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="">Seleccione un campo</option>
                                                <option value="ejecutivo">Ejecutivo</option>
                                                <option value="status">Estado</option>
                                                <option value="notes">Agregar Nota</option>
                                                {tableData.length > 0 && Object.keys(tableData[0])
                                                    .filter(key => !['_id', 'nombreCompleto', 'telefono', 'status', 'selected', 'notes', 'filename', 'importedAt', 'ejecutivo'].includes(key))
                                                    .map((key) => (
                                                        <option key={key} value={key}>{key}</option>
                                                    ))}
                                            </select>
                                        </div>
                                        
                                        {/* Field-specific inputs */}
                                        {massiveActionField === 'ejecutivo' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Seleccionar Ejecutivo
                                                </label>
                                                <Select
                                                    value={massiveActionEjecutivo}
                                                    onChange={(selected) => setMassiveActionEjecutivo(selected)}
                                                    options={ejecutivos}
                                                    placeholder="Seleccione un ejecutivo"
                                                    isClearable
                                                    className="text-sm"
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minHeight: '38px',
                                                            fontSize: '14px'
                                                        })
                                                    }}
                                                />
                                            </div>
                                        )}
                                        
                                        {massiveActionField === 'status' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Seleccionar Estado
                                                </label>
                                                <select
                                                    value={massiveActionStatus || ""}
                                                    onChange={(e) => setMassiveActionStatus(e.target.value ? parseInt(e.target.value) : null)}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    <option value="">Seleccione un estado</option>
                                                    {statuses.map(status => (
                                                        <option key={status.id} value={status.id}>{status.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        
                                        {massiveActionField === 'notes' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Escribir Nota
                                                </label>
                                                <textarea
                                                    value={massiveActionValue}
                                                    onChange={(e) => setMassiveActionValue(e.target.value)}
                                                    placeholder="Escribe la nota que se agregará a todos los registros seleccionados..."
                                                    rows={3}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        )}
                                        
                                        {massiveActionField && massiveActionField !== 'ejecutivo' && massiveActionField !== 'status' && massiveActionField !== 'notes' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Nuevo Valor
                                                </label>
                                                <input
                                                    type="text"
                                                    value={massiveActionValue}
                                                    onChange={(e) => setMassiveActionValue(e.target.value)}
                                                    placeholder={`Ingrese el nuevo valor para ${massiveActionField}`}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Apply button */}
                                    {massiveActionField && (
                                        <div className="mt-4 flex justify-end">
                                            <button
                                                onClick={handleMassiveAction}
                                                disabled={
                                                    (massiveActionField === 'ejecutivo' && !massiveActionEjecutivo) ||
                                                    (massiveActionField === 'status' && !massiveActionStatus) ||
                                                    (massiveActionField === 'notes' && !massiveActionValue.trim()) ||
                                                    (massiveActionField !== 'ejecutivo' && massiveActionField !== 'status' && massiveActionField !== 'notes' && !massiveActionValue.trim())
                                                }
                                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Aplicar a {selectedRows.length} registro(s)
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Seleccionar Plantilla de WhatsApp
                                    </label>
                                    <select
                                        value={selectedTemplate?._id || ""}
                                        onChange={handleTemplateChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">Seleccione una plantilla</option>
                                        {templates.map(template => (
                                            <option key={template._id} value={template._id}>
                                                {template.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {selectedTemplate && (
                                    <button
                                        onClick={() => setShowTemplateModal(true)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mt-6"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Ver Vista Previa
                                    </button>
                                )}
                                <button
                                    onClick={handleSendWhatsApp}
                                    disabled={!selectedTemplate || selectedVariables.length !== selectedTemplate?.parameters.length}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    Enviar WhatsApp
                                </button>
                            </div>
                            {selectedTemplate && (
                                <div className="mt-3 text-sm text-gray-600">
                                    <p>Variables seleccionadas: {selectedVariables.length} / {selectedTemplate.parameters.length}</p>
                                    {selectedVariables.length > 0 && (
                                        <p className="text-xs mt-1">
                                            {selectedVariables.map((v, i) => (
                                                <span key={i} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mr-1 mb-1">
                                                    {v}
                                                </span>
                                            ))}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Configuration UI - Admin Only (not department supervisor) */}
                {isAdminOnly() && (
                    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Configuraciones de Ventas Cruzadas</h2>
                                <p className="text-gray-600 mt-1">Gestionar configuraciones de departamentos</p>
                            </div>
                            <button
                                onClick={openCreateConfigModal}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Nueva Configuración
                            </button>
                        </div>

                        {/* Configurations List */}
                        {isLoadingConfigs ? (
                            <div className="text-center py-8">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                <p className="mt-2 text-gray-600">Cargando configuraciones...</p>
                            </div>
                        ) : configurations.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="mt-2">No hay configuraciones. Crea una nueva configuración para comenzar.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Nombre
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Descripción
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Departamentos
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Estado
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Fecha Creación
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Acciones
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {configurations.map((config) => (
                                            <tr key={config._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">{config.name}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-500 max-w-xs truncate">
                                                        {config.description || "-"}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-wrap gap-1">
                                                        {config.departments && config.departments.length > 0 ? (
                                                            config.departments.map((dept, index) => (
                                                                <span
                                                                    key={dept._id || index}
                                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                                >
                                                                    {dept.name}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="text-sm text-gray-400">Sin departamentos</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            config.isActive
                                                                ? "bg-green-100 text-green-800"
                                                                : "bg-red-100 text-red-800"
                                                        }`}
                                                    >
                                                        {config.isActive ? "Activo" : "Inactivo"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {config.createdAt
                                                        ? new Date(config.createdAt).toLocaleDateString("es-ES", {
                                                              year: "numeric",
                                                              month: "short",
                                                              day: "numeric",
                                                          })
                                                        : "-"}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => openEditConfigModal(config)}
                                                            className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                                            title="Editar"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => deleteConfiguration(config._id)}
                                                            className="text-red-600 hover:text-red-900 transition-colors"
                                                            title="Eliminar"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h2>
                    <div className={`grid grid-cols-1 gap-4 ${isUserAdmin() ? 'md:grid-cols-7' : 'md:grid-cols-6'}`}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Búsqueda General
                            </label>
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                placeholder="Buscar en todos los campos..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Estado
                            </label>
                            <select
                                value={filters.status || ""}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value ? parseInt(e.target.value) : null })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Todos los estados</option>
                                {statuses.map(status => (
                                    <option key={status.id} value={status.id}>{status.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nombre Completo
                            </label>
                            <input
                                type="text"
                                value={filters.nombreCompleto}
                                onChange={(e) => setFilters({ ...filters, nombreCompleto: e.target.value })}
                                placeholder="Filtrar por nombre..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Teléfono
                            </label>
                            <input
                                type="text"
                                value={filters.telefono}
                                onChange={(e) => setFilters({ ...filters, telefono: e.target.value })}
                                placeholder="Filtrar por teléfono..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Ejecutivo
                            </label>
                            <Select
                                value={ejecutivos.find(e => e.value === filters.ejecutivo) || null}
                                onChange={(selected) => setFilters({ ...filters, ejecutivo: selected?.value || null })}
                                options={ejecutivos}
                                isClearable
                                placeholder="Todos los ejecutivos"
                                className="text-sm"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '38px',
                                        fontSize: '14px'
                                    })
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Agente
                            </label>
                            <input
                                type="text"
                                value={filters.agente}
                                onChange={(e) => setFilters({ ...filters, agente: e.target.value })}
                                placeholder="Filtrar por agente..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        {isUserAdmin() && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Usuario Asignado
                                </label>
                                <input
                                    type="text"
                                    value={filters.assignedUser}
                                    onChange={(e) => setFilters({ ...filters, assignedUser: e.target.value })}
                                    placeholder="Filtrar por email de usuario..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        )}
                    </div>
                    <div className="mt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeOcultos}
                                onChange={(e) => {
                                    setIncludeOcultos(e.target.checked);
                                    // Reload data when toggling includeOcultos
                                    setTimeout(() => {
                                        loadDataFromAPI(selectedFile, pagination.page, pagination.pageSize, filters);
                                    }, 100);
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Incluir estados ocultos
                            </span>
                        </label>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 table-fixed">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <input
                                            type="checkbox"
                                            checked={selectedRows.length === paginatedData.length && paginatedData.length > 0}
                                            onChange={toggleAllRows}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                    </th>
                                    <th 
                                        className={`w-48 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                                            selectedTemplate ? 'cursor-pointer hover:bg-blue-100 transition-colors' : 'cursor-pointer hover:bg-gray-100 transition-colors'
                                        }`}
                                        onClick={() => {
                                            if (selectedTemplate) {
                                                handleColumnClickForVariable('nombreCompleto');
                                            } else {
                                                handleColumnSort('nombreCompleto');
                                            }
                                        }}
                                        title={selectedTemplate ? 'Click para agregar como variable' : 'Click para ordenar'}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>Nombre Completo</span>
                                            {sortBy === 'nombreCompleto' && (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    {sortOrder === 'asc' ? (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                    ) : (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    )}
                                                </svg>
                                            )}
                                        </div>
                                    </th>
                                    <th 
                                        className={`w-40 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                                            selectedTemplate ? 'cursor-pointer hover:bg-blue-100 transition-colors' : 'cursor-pointer hover:bg-gray-100 transition-colors'
                                        }`}
                                        onClick={() => {
                                            if (selectedTemplate) {
                                                handleColumnClickForVariable('telefono');
                                            } else {
                                                handleColumnSort('telefono');
                                            }
                                        }}
                                        title={selectedTemplate ? 'Click para agregar como variable' : 'Click para ordenar'}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>Teléfono</span>
                                            {sortBy === 'telefono' && (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    {sortOrder === 'asc' ? (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                    ) : (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    )}
                                                </svg>
                                            )}
                                        </div>
                                    </th>
                                    <th 
                                        className="w-40 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleColumnSort('status')}
                                        title="Click para ordenar"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>Estado</span>
                                            {sortBy === 'status' && (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    {sortOrder === 'asc' ? (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                    ) : (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    )}
                                                </svg>
                                            )}
                                        </div>
                                    </th>
                                    <th 
                                        className="w-48 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleColumnSort('ejecutivo')}
                                        title="Click para ordenar"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>Ejecutivo</span>
                                            {sortBy === 'ejecutivo' && (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    {sortOrder === 'asc' ? (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                    ) : (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    )}
                                                </svg>
                                            )}
                                        </div>
                                    </th>
                                    <th 
                                        className="w-48 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>Recordatorio</span>
                                        </div>
                                    </th>
                                    {tableData.length > 0 && Object.keys(tableData[0])
                                        .filter(key => !['_id', 'nombreCompleto', 'telefono', 'status', 'selected', 'notes', 'filename', 'importedAt', 'ejecutivo', 'agente'].includes(key))
                                        .map((key, index) => (
                                            <th 
                                                key={index} 
                                                className={`w-40 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                                                    selectedTemplate ? 'cursor-pointer hover:bg-blue-100 transition-colors' : 'cursor-pointer hover:bg-gray-100 transition-colors'
                                                }`}
                                                onClick={() => {
                                                    if (selectedTemplate) {
                                                        handleColumnClickForVariable(key);
                                                    } else {
                                                        handleColumnSort(key);
                                                    }
                                                }}
                                                title={selectedTemplate ? 'Click para agregar como variable' : 'Click para ordenar'}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span>{key}</span>
                                                    {sortBy === key && (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            {sortOrder === 'asc' ? (
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                            ) : (
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            )}
                                                        </svg>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    <th className="sticky right-0 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 z-10">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="100%" className="px-6 py-12 text-center">
                                            <div className="flex justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan="100%" className="px-6 py-12 text-center text-gray-500">
                                            No hay datos disponibles
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedData.map((row) => {
                                        const status = getStatusById(row.status);
                                        // Ensure status is always defined
                                        if (!status) {
                                            console.warn(`Status not found for row ${row._id} with statusId ${row.status}`);
                                        }
                                        const safeStatus = status || { id: 1, name: "Sin estado", color: "#6B7280" };
                                        return (
                                            <tr key={row._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRows.includes(row._id)}
                                                        onChange={() => toggleRowSelection(row._id)}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="w-48 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {editingCell?.rowId === row._id && editingCell?.fieldName === 'nombreCompleto' ? (
                                                        <input
                                                            type="text"
                                                            value={cellEditValue}
                                                            onChange={(e) => setCellEditValue(e.target.value)}
                                                            onBlur={() => handleCellBlur(row._id, 'nombreCompleto')}
                                                            onKeyDown={(e) => handleCellKeyDown(e, row._id, 'nombreCompleto')}
                                                            className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <div
                                                            onClick={() => handleCellEditStart(row._id, 'nombreCompleto', row.nombreCompleto)}
                                                            className="px-2 py-1 rounded hover:bg-gray-100 cursor-text min-h-[24px]"
                                                            title="Click para editar"
                                                        >
                                                            {row.nombreCompleto || "\u00A0"}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="w-40 px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {editingCell?.rowId === row._id && editingCell?.fieldName === 'telefono' ? (
                                                        <input
                                                            type="text"
                                                            value={cellEditValue}
                                                            onChange={(e) => setCellEditValue(e.target.value)}
                                                            onBlur={() => handleCellBlur(row._id, 'telefono')}
                                                            onKeyDown={(e) => handleCellKeyDown(e, row._id, 'telefono')}
                                                            className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <div
                                                            onClick={() => handleCellEditStart(row._id, 'telefono', row.telefono)}
                                                            className="px-2 py-1 rounded hover:bg-gray-100 cursor-text min-h-[24px]"
                                                            title="Click para editar"
                                                        >
                                                            {row.telefono || "\u00A0"}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="w-40 px-6 py-4 whitespace-nowrap">
                                                    <select
                                                        value={row.status || (statuses.length > 0 ? statuses[0]?.id : 1)}
                                                        onChange={(e) => updateRowStatus(row._id, parseInt(e.target.value))}
                                                        className="px-3 py-1 rounded-full text-xs font-medium text-white border-0 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer appearance-none"
                                                        style={{ backgroundColor: safeStatus.color }}
                                                        title={safeStatus.name}
                                                    >
                                                        {statuses.map(s => (
                                                            <option key={s.id} value={s.id} style={{ backgroundColor: s.color }}>
                                                                {s.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="w-48 px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <Select
                                                        value={row.ejecutivo ? ejecutivos.find(e => e.value === (row.ejecutivo?.value || row.ejecutivo?._id || row.ejecutivo)) : null}
                                                        onChange={async (selected) => {
                                                            const ejecutivoValue = selected ? {
                                                                value: selected.value,
                                                                label: selected.label,
                                                                selected: true
                                                            } : null;
                                                            
                                                            // Update local state immediately
                                                            setTableData(tableData.map(r => 
                                                                r._id === row._id ? { ...r, ejecutivo: ejecutivoValue } : r
                                                            ));
                                                            
                                                            // Save to API
                                                            try {
                                                                const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/${row._id}`;
                                                                const config = {
                                                                    headers: {
                                                                        "x-access-token": user.accessToken,
                                                                        "Content-Type": "application/json",
                                                                    },
                                                                };
                                                                await axios.put(url, { ejecutivo: ejecutivoValue }, config);
                                                            } catch (error) {
                                                                console.error("Error updating ejecutivo:", error);
                                                                toast.error("Error al actualizar el ejecutivo");
                                                                // Revert on error
                                                                setTableData(tableData.map(r => 
                                                                    r._id === row._id ? { ...r, ejecutivo: row.ejecutivo } : r
                                                                ));
                                                            }
                                                        }}
                                                        options={ejecutivos}
                                                        isClearable
                                                        placeholder="Seleccionar ejecutivo"
                                                        className="text-sm"
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                minHeight: '32px',
                                                                fontSize: '12px'
                                                            }),
                                                            menu: (base) => ({
                                                                ...base,
                                                                fontSize: '12px'
                                                            })
                                                        }}
                                                    />
                                                </td>
                                                <td className="w-48 px-6 py-4 text-sm text-gray-500">
                                                    {(() => {
                                                        // Get the first reminder from notes
                                                        if (row.notes && Array.isArray(row.notes)) {
                                                            const reminders = row.notes.filter(note => note.reminder);
                                                            if (reminders.length > 0) {
                                                                // Sort by reminder date and get the first one
                                                                const sortedReminders = [...reminders].sort((a, b) => 
                                                                    new Date(a.reminder) - new Date(b.reminder)
                                                                );
                                                                const firstReminder = sortedReminders[0];
                                                                const reminderDate = new Date(firstReminder.reminder);
                                                                const isPast = reminderDate < new Date();
                                                                const noteText = firstReminder.text || '';
                                                                
                                                                return (
                                                                    <div className="flex flex-col gap-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <svg className={`w-4 h-4 flex-shrink-0 ${isPast ? 'text-red-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                            </svg>
                                                                            <span className={`${isPast ? 'text-red-600 font-medium' : 'text-orange-600'} whitespace-nowrap`}>
                                                                                {formatTimestamp(firstReminder.reminder)}
                                                                            </span>
                                                                            {isPast && (
                                                                                <span className="text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded flex-shrink-0">
                                                                                    Vencido
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {noteText && (
                                                                            <p className="text-xs text-gray-600 truncate" title={noteText}>
                                                                                {noteText}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                        }
                                                        return <span className="text-gray-400">Sin recordatorio</span>;
                                                    })()}
                                                </td>
                                                {Object.keys(row)
                                                    .filter(key => !['_id', 'nombreCompleto', 'telefono', 'status', 'selected', 'notes', 'filename', 'importedAt', 'ejecutivo', 'agente'].includes(key))
                                                    .map((key, index) => {
                                                        // Ensure value is renderable (not an object or array)
                                                        const value = row[key];
                                                        let displayValue = "\u00A0";
                                                        if (value !== null && value !== undefined && value !== "") {
                                                            if (typeof value === 'object') {
                                                                // If it's an object or array, convert to string
                                                                displayValue = JSON.stringify(value);
                                                            } else {
                                                                displayValue = String(value);
                                                            }
                                                        }
                                                        const isEditing = editingCell?.rowId === row._id && editingCell?.fieldName === key;
                                                        
                                                        return (
                                                            <td key={index} className="w-40 px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {isEditing ? (
                                                                    <input
                                                                        type="text"
                                                                        value={cellEditValue}
                                                                        onChange={(e) => setCellEditValue(e.target.value)}
                                                                        onBlur={() => handleCellBlur(row._id, key)}
                                                                        onKeyDown={(e) => handleCellKeyDown(e, row._id, key)}
                                                                        className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        onClick={() => handleCellEditStart(row._id, key, value)}
                                                                        className="px-2 py-1 rounded hover:bg-gray-100 cursor-text min-h-[24px]"
                                                                        title="Click para editar"
                                                                    >
                                                                        {displayValue}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                <td className="sticky right-0 px-6 py-4 whitespace-nowrap text-sm font-medium bg-white z-10 hover:bg-gray-50">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => openNotesModal(row)}
                                                            className="text-blue-600 hover:text-blue-900 transition-colors"
                                                            title="Escribir una nota"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        {row.notes && row.notes.length > 0 && (
                                                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                                {row.notes.length}
                                                            </span>
                                                        )}
                                                        {row.notes && row.notes.some(note => note.reminder) && (
                                                            <button
                                                                onClick={() => setShowRemindersCalendar(true)}
                                                                className="text-orange-600 hover:text-orange-900 transition-colors"
                                                                title="Ver recordatorios"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleSendWhatsApp([row._id])}
                                                            className="text-green-600 hover:text-green-900 transition-colors"
                                                            title="Enviar WhatsApp"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.total > 0 && (
                        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                            <div className="flex items-center gap-4">
                                <div className="text-sm text-gray-700">
                                    Mostrando {(pagination.page - 1) * pagination.pageSize + 1} a {Math.min(pagination.page * pagination.pageSize, pagination.total)} de {pagination.total} resultados
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-700">Registros por página:</label>
                                    <select
                                        value={pagination.pageSize}
                                        onChange={async (e) => {
                                            const newPageSize = parseInt(e.target.value);
                                            setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
                                            await loadDataFromAPI(selectedFile, 1, newPageSize);
                                        }}
                                        disabled={isLoading}
                                        className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                    >
                                        <option value={15}>15</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={async () => {
                                        const newPage = Math.max(1, pagination.page - 1);
                                        setPagination(prev => ({ ...prev, page: newPage }));
                                        await loadDataFromAPI(selectedFile, newPage, pagination.pageSize);
                                    }}
                                    disabled={pagination.page === 1 || isLoading}
                                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                >
                                    Anterior
                                </button>
                                <button
                                    onClick={async () => {
                                        const newPage = pagination.page + 1;
                                        setPagination(prev => ({ ...prev, page: newPage }));
                                        await loadDataFromAPI(selectedFile, newPage, pagination.pageSize);
                                    }}
                                    disabled={pagination.page * pagination.pageSize >= pagination.total || isLoading}
                                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Column Mapping Modal */}
            {showColumnMapping && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Mapear Columnas del Excel</h2>
                        <p className="text-gray-600 mb-6">Seleccione las columnas que corresponden a cada campo requerido</p>
                        
                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombre Completo <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedColumns.nombreCompleto || ""}
                                    onChange={(e) => setSelectedColumns({ ...selectedColumns, nombreCompleto: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Seleccione una columna</option>
                                    {availableColumns.map(col => (
                                        <option key={col} value={col}>{col}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Teléfono <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedColumns.telefono || ""}
                                    onChange={(e) => setSelectedColumns({ ...selectedColumns, telefono: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Seleccione una columna</option>
                                    {availableColumns.map(col => (
                                        <option key={col} value={col}>{col}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Agente <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedColumns.agente || ""}
                                    onChange={(e) => setSelectedColumns({ ...selectedColumns, agente: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Seleccione una columna</option>
                                    {availableColumns.map(col => (
                                        <option key={col} value={col}>{col}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Otras Columnas (Opcional)
                                </label>
                                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                                    {availableColumns
                                        .filter(col => col !== selectedColumns.nombreCompleto && col !== selectedColumns.telefono && col !== selectedColumns.agente)
                                        .map(col => {
                                            const existing = selectedColumns.otherColumns.find(c => c.column === col);
                                            return (
                                                <label key={col} className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={existing?.selected || false}
                                                        onChange={(e) => {
                                                            const otherCols = selectedColumns.otherColumns.filter(c => c.column !== col);
                                                            if (e.target.checked) {
                                                                setSelectedColumns({
                                                                    ...selectedColumns,
                                                                    otherColumns: [...otherCols, { column: col, selected: true }]
                                                                });
                                                            } else {
                                                                setSelectedColumns({
                                                                    ...selectedColumns,
                                                                    otherColumns: otherCols
                                                                });
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-700">{col}</span>
                                                </label>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowColumnMapping(false);
                                    setRawExcelData([]);
                                    setSelectedColumns({ nombreCompleto: null, telefono: null, agente: null, otherColumns: [] });
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleColumnMapping}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Importar Datos
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Status Modal */}
            {showStatusModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Crear Nuevo Estado</h2>
                        
                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombre del Estado
                                </label>
                                <input
                                    type="text"
                                    value={newStatusName}
                                    onChange={(e) => setNewStatusName(e.target.value)}
                                    placeholder="Ej: En Seguimiento"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Color
                                </label>
                                <input
                                    type="color"
                                    value={newStatusColor}
                                    onChange={(e) => setNewStatusColor(e.target.value)}
                                    className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowStatusModal(false);
                                    setNewStatusName("");
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={createStatus}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                Crear Estado
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Statuses Modal */}
            {showManageStatusesModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">Gestionar Estados</h2>
                            <button
                                onClick={() => {
                                    setShowManageStatusesModal(false);
                                    setEditingStatus(null);
                                    cancelEditingStatus();
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {statuses.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No hay estados disponibles</p>
                        ) : (
                            <div className="space-y-4">
                                {statuses.map((status) => (
                                    <div key={status.id} className="border border-gray-200 rounded-lg p-4">
                                        {editingStatus && editingStatus.id === status.id ? (
                                            // Edit mode
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Nombre del Estado
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={editStatusName}
                                                        onChange={(e) => setEditStatusName(e.target.value)}
                                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Color
                                                    </label>
                                                    <input
                                                        type="color"
                                                        value={editStatusColor}
                                                        onChange={(e) => setEditStatusColor(e.target.value)}
                                                        className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
                                                    />
                                                </div>
                                                <div className="flex justify-end gap-3">
                                                    <button
                                                        onClick={cancelEditingStatus}
                                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                                    >
                                                        Cancelar
                                                    </button>
                                                    <button
                                                        onClick={updateStatus}
                                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                                    >
                                                        Guardar Cambios
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            // View mode
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div
                                                        className="w-8 h-8 rounded-full border-2 border-gray-300"
                                                        style={{ backgroundColor: status.color }}
                                                    ></div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-medium text-gray-900">{status.name}</p>
                                                            {status.ocultar && (
                                                                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                                                    Oculto
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-500">ID: {status.id}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={status.ocultar || false}
                                                            onChange={(e) => toggleOcultarStatus(status.id, e.target.checked)}
                                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                        />
                                                        <span className="text-sm text-gray-700">Ocultar</span>
                                                    </label>
                                                    <button
                                                        onClick={() => startEditingStatus(status)}
                                                        className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                        Editar
                                                    </button>
                                                    <button
                                                        onClick={() => deleteStatus(status.id)}
                                                        className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center gap-1"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                        Eliminar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Manage Files Modal */}
            {showManageFilesModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">Gestionar Archivos</h2>
                            <button
                                onClick={() => {
                                    setShowManageFilesModal(false);
                                    setSelectedFilesToDelete([]);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {savedFiles.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No hay archivos guardados</p>
                        ) : (
                            <>
                                <div className="mb-4 flex items-center justify-between">
                                    <p className="text-sm text-gray-600">
                                        {selectedFilesToDelete.length > 0 
                                            ? `${selectedFilesToDelete.length} archivo(s) seleccionado(s)`
                                            : "Seleccione uno o más archivos para eliminar"}
                                    </p>
                                    {selectedFilesToDelete.length > 0 && (
                                        <button
                                            onClick={() => setSelectedFilesToDelete([])}
                                            className="text-sm text-gray-600 hover:text-gray-800"
                                        >
                                            Deseleccionar todo
                                        </button>
                                    )}
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedFilesToDelete.length === savedFiles.length && savedFiles.length > 0}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedFilesToDelete(savedFiles.map((file, index) => index));
                                                            } else {
                                                                setSelectedFilesToDelete([]);
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Nombre del Archivo
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Fecha de Importación
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Total de Registros
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {savedFiles.map((file, index) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedFilesToDelete.includes(index)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedFilesToDelete([...selectedFilesToDelete, index]);
                                                                } else {
                                                                    setSelectedFilesToDelete(selectedFilesToDelete.filter(i => i !== index));
                                                                }
                                                            }}
                                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {file.filename}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {formatTimestamp(file.importedAt)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {file.count || 0}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setShowManageFilesModal(false);
                                            setSelectedFilesToDelete([]);
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleDeleteFiles}
                                        disabled={selectedFilesToDelete.length === 0 || isLoading}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Eliminar ({selectedFilesToDelete.length})
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Template Preview Modal */}
            {showTemplateModal && selectedTemplate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">Vista Previa de Plantilla</h2>
                            <button
                                onClick={() => setShowTemplateModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left side - Column selection */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Seleccionar Variables</h3>
                                <p className="text-sm text-gray-600 mb-4">
                                    Haga clic en los nombres de las columnas para agregar variables a la plantilla.
                                    Variables seleccionadas: {selectedVariables.length} / {selectedTemplate.parameters.length}
                                </p>
                                
                                <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-3">
                                    {tableData.length > 0 && Object.keys(tableData[0])
                                        .filter(key => !['_id', 'status', 'selected'].includes(key))
                                        .map((key, index) => {
                                            const isSelected = selectedVariables.includes(key) || selectedButtonParameters.includes(key);
                                            return (
                                                <button
                                                    key={index}
                                                    onClick={() => handleColumnClickForVariable(key)}
                                                    disabled={isSelected || (selectedVariables.length >= selectedTemplate.parameters.length && 
                                                        (!selectedTemplate.buttonparameters || selectedButtonParameters.length >= selectedTemplate.buttonparameters.length))}
                                                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                                        isSelected 
                                                            ? 'bg-green-100 text-green-800 cursor-not-allowed' 
                                                            : selectedVariables.length >= selectedTemplate.parameters.length && 
                                                              (!selectedTemplate.buttonparameters || selectedButtonParameters.length >= selectedTemplate.buttonparameters.length)
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            : 'bg-blue-50 text-blue-800 hover:bg-blue-100 cursor-pointer'
                                                    }`}
                                                >
                                                    {key} {isSelected && '✓'}
                                                </button>
                                            );
                                        })}
                                </div>

                                {selectedVariables.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-gray-700 mb-2">Variables seleccionadas:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedVariables.map((v, i) => (
                                                <span key={i} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                                                    {i + 1}: {v}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right side - WhatsApp preview */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Vista Previa</h3>
                                <div className="bg-[#e5ddd5] rounded-lg p-4" style={{ backgroundImage: "url('/chatbg.png')" }}>
                                    <div className="flex flex-col">
                                        <div className="bg-white h-6 w-full rounded-t-lg">
                                            <p className="text-center font-bold text-md">{selectedTemplate.name}</p>
                                        </div>
                                        <div className="p-4">
                                            <div className="flex-1 w-full bg-white p-4 rounded-b-lg rounded-tr-lg max-w-[300px] shadow-sm">
                                                {selectedTemplate.image && (
                                                    <img src={selectedTemplate.image} alt="Template" className="w-full mb-2 rounded" />
                                                )}
                                                <p 
                                                    style={{ fontFamily: 'arial', fontSize: '13px' }} 
                                                    dangerouslySetInnerHTML={{ __html: selectedTemplate.body }}
                                                />
                                                {selectedTemplate.buttonparameters && selectedTemplate.buttonparameters.length > 0 && (
                                                    selectedTemplate.buttonparameters.map((buttonparameter, index) => (
                                                        <div className="flex flex-row border-t border-gray-300 px-4 py-2 mt-2" key={index}>
                                                            <svg fill="#000ba3" height="15px" width="15px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26.676 26.676">
                                                                <g>
                                                                    <path d="M26.105,21.891c-0.229,0-0.439-0.131-0.529-0.346l0,0c-0.066-0.156-1.716-3.857-7.885-4.59 c-1.285-0.156-2.824-0.236-4.693-0.25v4.613c0,0.213-0.115,0.406-0.304,0.508c-0.188,0.098-0.413,0.084-0.588-0.033L0.254,13.815 C0.094,13.708,0,13.528,0,13.339c0-0.191,0.094-0.365,0.254-0.477l11.857-7.979c0.175-0.121,0.398-0.129,0.588-0.029 c0.19,0.102,0.303,0.295,0.303,0.502v4.293c2.578,0.336,13.674,2.33,13.674,11.674c0,0.271-0.191,0.508-0.459,0.562 C26.18,21.891,26.141,21.891,26.105,21.891z"></path>
                                                                </g>
                                                            </svg>
                                                            <span className="ml-2 text-sm">
                                                                {selectedButtonParameters[index] ? selectedButtonParameters[index] : buttonparameter.label || `BOTON ${index + 1}`}
                                                            </span>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowTemplateModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Cerrar
                            </button>
                            <button
                                onClick={handleSendWhatsApp}
                                disabled={selectedVariables.length !== selectedTemplate.parameters.length}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Enviar WhatsApp
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notes Modal */}
            {showNotesModal && selectedRowForNotes && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Notas</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {selectedRowForNotes.nombreCompleto || "Sin nombre"} - {selectedRowForNotes.telefono || "Sin teléfono"}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowNotesModal(false);
                                    setSelectedRowForNotes(null);
                                    setNotes([]);
                                    setNewNote("");
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Existing Notes */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                Notas Existentes ({notes.length})
                            </h3>
                            {notes.length === 0 ? (
                                <p className="text-gray-500 text-sm italic">No hay notas aún</p>
                            ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {notes
                                        .filter(note => note && typeof note === 'object')
                                        .map((note, index) => {
                                            // Ensure all note properties are strings
                                            const noteUser = note.user ? String(note.user) : "Usuario";
                                            const noteText = note.text ? String(note.text) : "";
                                            const noteTimestamp = note.timestamp ? String(note.timestamp) : new Date().toISOString();
                                            
                                            return (
                                                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                                                                {noteUser ? noteUser.charAt(0).toUpperCase() : "U"}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">
                                                                    {noteUser}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {formatTimestamp(noteTimestamp)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                        {noteText}
                                                    </p>
                                                    {note.reminder && (
                                                        <div className="mt-2 flex items-center gap-2 text-xs text-orange-600">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                            <span>Recordatorio: {formatTimestamp(note.reminder)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        </div>

                        {/* Add New Note */}
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Escribir una Nueva Nota</h3>
                            <div className="space-y-3">
                                <textarea
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    placeholder="Escribe tu nota aquí..."
                                    rows={4}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Recordatorio - Fecha <span className="text-gray-400 text-xs">(Opcional)</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={noteReminder}
                                            onChange={(e) => setNoteReminder(e.target.value)}
                                            min={new Date().toISOString().split('T')[0]}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Recordatorio - Hora <span className="text-gray-400 text-xs">(Opcional)</span>
                                        </label>
                                        <input
                                            type="time"
                                            value={noteReminderTime}
                                            onChange={(e) => setNoteReminderTime(e.target.value)}
                                            disabled={!noteReminder}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setShowNotesModal(false);
                                            setSelectedRowForNotes(null);
                                            setNotes([]);
                                            setNewNote("");
                                            setNoteReminder("");
                                            setNoteReminderTime("");
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Cerrar
                                    </button>
                                    <button
                                        onClick={handleAddNote}
                                        disabled={!newNote.trim()}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Agregar Nota
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reminders Calendar Modal */}
            {showRemindersCalendar && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">Recordatorios</h2>
                            <button
                                onClick={() => setShowRemindersCalendar(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Get all reminders from all rows */}
                        {(() => {
                            const allReminders = [];
                            tableData.forEach(row => {
                                if (row.notes && Array.isArray(row.notes)) {
                                    row.notes.forEach(note => {
                                        if (note.reminder) {
                                            allReminders.push({
                                                ...note,
                                                rowId: row._id,
                                                nombreCompleto: row.nombreCompleto,
                                                telefono: row.telefono
                                            });
                                        }
                                    });
                                }
                            });

                            // Sort by reminder date
                            allReminders.sort((a, b) => new Date(a.reminder) - new Date(b.reminder));

                            if (allReminders.length === 0) {
                                return <p className="text-gray-500 text-center py-8">No hay recordatorios programados</p>;
                            }

                            return (
                                <div className="space-y-3">
                                    {allReminders.map((reminder, index) => {
                                        const reminderDate = new Date(reminder.reminder);
                                        const isPast = reminderDate < new Date();
                                        
                                        return (
                                            <div 
                                                key={index} 
                                                className={`bg-gray-50 rounded-lg p-4 border ${isPast ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                            <span className={`text-sm font-semibold ${isPast ? 'text-red-600' : 'text-orange-600'}`}>
                                                                {formatTimestamp(reminder.reminder)}
                                                            </span>
                                                            {isPast && (
                                                                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                                                    Vencido
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm font-medium text-gray-900 mb-1">
                                                            {reminder.nombreCompleto || 'Sin nombre'}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mb-2">
                                                            {reminder.telefono || 'Sin teléfono'}
                                                        </p>
                                                        <p className="text-sm text-gray-700">
                                                            {reminder.text}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-2">
                                                            Por: {reminder.user || 'Usuario'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Configuration Modal - Create/Edit */}
            {showConfigModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingConfig ? "Editar Configuración" : "Nueva Configuración"}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowConfigModal(false);
                                    resetConfigForm();
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Name Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombre <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={configFormData.name}
                                    onChange={(e) => setConfigFormData({ ...configFormData, name: e.target.value })}
                                    placeholder="Ej: Configuración de Ventas"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    required
                                />
                            </div>

                            {/* Description Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    value={configFormData.description}
                                    onChange={(e) => setConfigFormData({ ...configFormData, description: e.target.value })}
                                    placeholder="Descripción de la configuración (opcional)"
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                                />
                            </div>

                            {/* Departments Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Departamentos <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    isMulti
                                    value={configFormData.departments}
                                    onChange={(selected) => setConfigFormData({ ...configFormData, departments: selected || [] })}
                                    options={departments.map(dept => ({
                                        value: dept._id,
                                        label: dept.name
                                    }))}
                                    placeholder="Seleccione uno o más departamentos"
                                    className="text-sm"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '38px',
                                            fontSize: '14px'
                                        })
                                    }}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Seleccione al menos un departamento
                                </p>
                            </div>

                            {/* Is Active Field */}
                            <div>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={configFormData.isActive}
                                        onChange={(e) => setConfigFormData({ ...configFormData, isActive: e.target.checked })}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        Configuración activa
                                    </span>
                                </label>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => {
                                        setShowConfigModal(false);
                                        resetConfigForm();
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                    disabled={isLoadingConfigs}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={editingConfig ? updateConfiguration : createConfiguration}
                                    disabled={isLoadingConfigs || !configFormData.name.trim() || !configFormData.departments || configFormData.departments.length === 0}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                >
                                    {isLoadingConfigs ? (
                                        <>
                                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            {editingConfig ? "Actualizando..." : "Creando..."}
                                        </>
                                    ) : (
                                        editingConfig ? "Actualizar" : "Crear"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden file input */}
            <input
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                        readExcelFile(file);
                    }
                }}
            />

            <ToastContainer position="top-right" />
        </div>
    );
}

